import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const MINUS_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
</svg>`;

const PLUS_SVG = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
  <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
</svg>`;

const DELETE_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
<path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {boolean} */
  #isRendering = false;

  /** @type {string | undefined} */
  #totalLabelText;

  /** @type {string | undefined} */
  #checkoutButtonText;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#captureStaticTexts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    const isInitialLoad = event?.detail?.data?.isInitialLoad || false;

    if (this.hasAttribute('auto-open') && !isInitialLoad) {
      this.showDialog();
    }

    this.#loadAndRenderCart();
  };

  open() {
    this.showDialog();

    this.#loadAndRenderCart();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #captureStaticTexts() {
    if (this.#totalLabelText && this.#checkoutButtonText) return;

    const totalLabel = this.querySelector('.cart__total-label');
    if (totalLabel && totalLabel.textContent) {
      this.#totalLabelText = totalLabel.textContent.trim();
    }

    const checkoutButton = this.querySelector('.cart__checkout-button');
    if (checkoutButton && checkoutButton.textContent) {
      this.#checkoutButtonText = checkoutButton.textContent.trim();
    }
  }

  async #loadAndRenderCart() {
    if (this.#isRendering) return;
    this.#isRendering = true;

    try {
      const shared = window.SharedCart;
      if (!shared) return;

      await shared.getOrCreateCart();
      const cart = await shared.fetchCart();
      await this.#renderCart(cart);
    } catch (error) {
      console.error('Failed to render shared cart drawer', error);
    } finally {
      this.#isRendering = false;
    }
  }

  /**
   * @param {any} cart
   */
  async #renderCart(cart) {
    const dialog = this.querySelector('.cart-drawer__dialog');
    const content = this.querySelector('.cart-drawer__content');

    if (!dialog || !content) return;

    const lines = cart?.lines?.edges || [];
    const hasLines = Array.isArray(lines) && lines.length > 0;

    dialog.classList.toggle('cart-drawer--empty', !hasLines);
    dialog.setAttribute(
      'aria-labelledby',
      hasLines ? 'cart-drawer-heading' : 'cart-drawer-heading-empty'
    );

    const tableBody = content.querySelector('.cart-items__table tbody');
    const captionSpan = content.querySelector('.cart-items__table caption span');
    const totalNode = content.querySelector('text-component[ref="cartTotal"], .cart__total-value');
    const emptyHeading = this.querySelector('#cart-drawer-heading-empty');
    const fullHeading = this.querySelector('#cart-drawer-heading');
    const bubbleCount = this.querySelector('.cart-bubble__text-count');
    const bubbleSr = this.querySelector('#cart-bubble-text .visually-hidden');
    const bubbleRoot = this.querySelector('.cart-bubble');

    if (!tableBody || !captionSpan || !totalNode) return;

    if (!hasLines) {
      tableBody.innerHTML = '';

      if (emptyHeading instanceof HTMLElement) emptyHeading.style.removeProperty('display');
      if (fullHeading instanceof HTMLElement) fullHeading.style.display = 'none';

      if (bubbleCount) {
        bubbleCount.textContent = '0';
        bubbleCount.classList.add('hidden');
      }
      if (bubbleSr) {
        bubbleSr.textContent = `Total items in cart: 0`;
      }
      if (bubbleRoot) {
        bubbleRoot.classList.add('visually-hidden');
      }

      return;
    }

    this.#captureStaticTexts();

    // 1) Replace table body rows with Storefront cart lines
    tableBody.innerHTML = this.#buildCartRows(cart);
    this.#attachLineHandlers(tableBody);

    // 2) Update headings
    if (emptyHeading instanceof HTMLElement) emptyHeading.style.display = 'none';
    if (fullHeading instanceof HTMLElement) fullHeading.style.removeProperty('display');

    // 3) Hide prices initially — Storefront API prices don't reflect Markets/discounts.
    //    Show empty placeholders until Shopify session cart prices are fetched.
    captionSpan.textContent = '';
    if (totalNode instanceof HTMLElement) {
      totalNode.textContent = '';
      totalNode.setAttribute('value', '');
    }
    // Hide line totals in the right column until sync
    tableBody.querySelectorAll('td.cart-items__price span:not(.visually-hidden)').forEach((el) => {
      el.textContent = '';
    });

    // 4) Fetch correct prices from Shopify session cart and populate.
    await this.#syncDrawerPricesFromShopify(content, {
      tableBody,
      drawerCaptionSpan: captionSpan,
      drawerTotalNode: totalNode,
      sharedCart: cart,
    });

    // 4) Update bubble (visible + SR text)
    const count = cart.totalQuantity || 0;
    if (bubbleCount) {
      bubbleCount.textContent = String(count);
      bubbleCount.classList.toggle('hidden', count === 0);
    }
    if (bubbleSr) {
      bubbleSr.textContent = `Total items in cart: ${count}`;
    }
    if (bubbleRoot) {
      bubbleRoot.classList.toggle('visually-hidden', count === 0);
    }
  }

  /**
   * @param {any} cart
   * @returns {string}
   */
  #buildCartRows(cart) {
    const lines = cart?.lines?.edges || [];
    const totalCurrency = cart?.cost?.totalAmount?.currencyCode || 'USD';

    return lines
      .map((edge) => {
        const line = edge.node;
        const lineId = line.id;
        const quantity = line.quantity;
        const merchandise = line.merchandise || {};
        const variantTitle = merchandise.title || '';
        const product = merchandise.product || {};
        const productTitle = product.title || '';
        const productHandle = product.handle || '';
        const merchandiseId = merchandise.id || '';
        const variantIdNumeric = (() => {
          if (typeof merchandiseId !== 'string' || !merchandiseId.length) return null;
          const parts = merchandiseId.split('/');
          const last = parts[parts.length - 1];
          const id = parseInt(last, 10);
          return isNaN(id) ? null : id;
        })();
        const image = merchandise.image || {};
        const lineAmount = line.cost?.totalAmount;
        const lineCurrency = lineAmount?.currencyCode || totalCurrency;
        const lineTotal = this.#formatLineMoney(lineAmount?.amount || '0', lineCurrency);
        const productUrl = productHandle ? `/products/${productHandle}` : '#';
        
        // Get unit price and compare-at price for discount display
        const unitPrice = merchandise.price;
        const compareAtPrice = merchandise.compareAtPrice;
        const hasDiscount = compareAtPrice && 
          parseFloat(compareAtPrice.amount) > parseFloat(unitPrice?.amount || '0');

        const imageHtml = image?.url
          ? `
              <a
                href="${productUrl}"
                class="cart-items__media-container"
              >
                <img
                  src="${image.url}"
                  alt="${(image.altText || productTitle || '').replace(/"/g, '&quot;')}"
                  class="cart-items__media-image border-style"
                  loading="lazy"
                >
              </a>
            `
          : '';

        const safeVariant =
          variantTitle && variantTitle.toLowerCase() !== 'default title'
            ? `<p class="cart-items__variant">${this.#escapeHtml(variantTitle)}</p>`
            : '';

        // Price block: leave empty — correct prices will be filled by #syncDrawerPricesFromShopify
        // to avoid flashing wrong Storefront API prices (which don't reflect Markets/discounts).
        const priceBlock = '';

        return `
          <tr
            role="row"
            class="cart-items__table-row"
            data-shared-cart-line-id="${lineId}"
            data-variant-id="${variantIdNumeric ?? ''}"
          >
            <td
              class="cart-items__media"
              role="cell"
            >
              ${imageHtml}
            </td>
            <td
              class="cart-items__details cart-primary-typography"
              role="cell"
            >
              <p>
                <a
                  href="${productUrl}"
                  class="cart-items__title"
                >
                  ${this.#escapeHtml(productTitle)}
                </a>
              </p>
              ${safeVariant}
              ${priceBlock}
            </td>
            <td
              class="cart-items__quantity"
              role="cell"
            >
              <div class="cart-items__quantity-controls">
                <div class="quantity-selector cart-primary-typography">
                  <button
                    class="button quantity-minus button-unstyled"
                    type="button"
                    data-shared-cart-qty-change="decrease"
                  >
                    <span class="visually-hidden">Decrease quantity</span>
                    <span class="svg-wrapper icon-plus">
                      ${MINUS_SVG}
                    </span>
                  </button>
                  <input
                    class="shared-cart-quantity-input"
                    type="number"
                    min="1"
                    value="${quantity}"
                    aria-label="Quantity"
                  >
                  <button
                    class="button quantity-plus button-unstyled"
                    type="button"
                    data-shared-cart-qty-change="increase"
                  >
                    <span class="visually-hidden">Increase quantity</span>
                    <span class="svg-wrapper icon-plus">
                      ${PLUS_SVG}
                    </span>
                  </button>
                </div>

                <button
                  class="button button--tertiary cart-items__remove"
                  type="button"
                  data-shared-cart-remove
                >
                  ${DELETE_SVG}
                  <span class="visually-hidden">Remove</span>
                </button>
              </div>
            </td>
            <td
              class="cart-items__price cart-secondary-typography"
              role="cell"
            >
              <span>${lineTotal}</span>
            </td>
          </tr>
        `;
      })
      .join('');
  }

  /**
   * @param {HTMLElement} root
   */
  #attachLineHandlers(root) {
    const shared = window.SharedCart;
    if (!shared) return;

    const rows = root.querySelectorAll('[data-shared-cart-line-id]');
    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const lineId = row.getAttribute('data-shared-cart-line-id');
      if (!lineId) return;

      const input = row.querySelector('.shared-cart-quantity-input');
      const removeButton = row.querySelector('[data-shared-cart-remove]');
      const buttons = row.querySelectorAll('[data-shared-cart-qty-change]');

      if (input instanceof HTMLInputElement) {
        input.addEventListener('change', () => {
          const value = parseInt(input.value || '0', 10);
          const quantity = Number.isFinite(value) && value >= 0 ? value : 0;
          shared
            .updateLine(lineId, quantity)
            .then((cart) => {
              this.#renderCart(cart);
            })
            .catch((error) => {
              console.error('Shared cart update failed', error);
            });
        });
      }

      buttons.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.addEventListener('click', () => {
          if (!(input instanceof HTMLInputElement)) return;
          const current = parseInt(input.value || '0', 10) || 0;
          const direction = button.getAttribute('data-shared-cart-qty-change');
          let next = current;
          if (direction === 'increase') next = current + 1;
          if (direction === 'decrease') next = current - 1;
          if (next < 0) next = 0;

          shared
            .updateLine(lineId, next)
            .then((cart) => {
              this.#renderCart(cart);
            })
            .catch((error) => {
              console.error('Shared cart update failed', error);
            });
        });
      });

      if (removeButton instanceof HTMLButtonElement) {
        removeButton.addEventListener('click', () => {
          shared
            .removeLine(lineId)
            .then((cart) => {
              this.#renderCart(cart);
            })
            .catch((error) => {
              console.error('Shared cart remove failed', error);
            });
        });
      }
    });

    const checkoutButton = this.querySelector('.cart__checkout-button');
    if (
      (checkoutButton instanceof HTMLButtonElement || checkoutButton instanceof HTMLAnchorElement) &&
      !checkoutButton.dataset.sharedCheckoutBound
    ) {
      checkoutButton.dataset.sharedCheckoutBound = '1';

      checkoutButton.addEventListener('click', async (event) => {
        event.preventDefault();
        try {
          const cart = await shared.fetchCart();
          if (cart?.checkoutUrl) {
            window.location.href = cart.checkoutUrl;
          } else if (checkoutButton instanceof HTMLAnchorElement && checkoutButton.href) {
            window.location.href = checkoutButton.href;
          } else if (checkoutButton instanceof HTMLButtonElement && checkoutButton.form) {
            checkoutButton.form.submit();
          }
        } catch (e) {
          if (checkoutButton instanceof HTMLAnchorElement && checkoutButton.href) {
            window.location.href = checkoutButton.href;
          } else if (checkoutButton instanceof HTMLButtonElement && checkoutButton.form) {
            checkoutButton.form.submit();
          }
        }
      });
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #formatLineMoney(amountString, currency) {
    const amountNumber = Number(amountString || '0');
    if (!Number.isFinite(amountNumber)) return amountString;

    const cents = Math.round(amountNumber * 100);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Theme?.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.Theme.moneyFormat);
      } catch (e) {
        // fall through
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(amountNumber);
    } catch {
      return amountNumber.toFixed(2) + ' ' + currency;
    }
  }

  #formatTotalMoney(amountString, currency) {
    const amountNumber = Number(amountString || '0');
    if (!Number.isFinite(amountNumber)) return amountString;

    const cents = Math.round(amountNumber * 100);

    if (
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function' &&
      window.Theme?.moneyWithCurrencyFormat
    ) {
      try {
        return window.Shopify.formatMoney(cents, window.Theme.moneyWithCurrencyFormat);
      } catch (e) {
        // fall through
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(amountNumber);
    } catch {
      return amountNumber.toFixed(2) + ' ' + currency;
    }
  }

  /**
   * Fetch Shopify-rendered cart HTML and override visible price texts inside the drawer.
   * This ensures Markets "tax display" settings are reflected identically to `/cart`.
   *
   * @param {HTMLElement} content
   * @param {{
   *  tableBody: HTMLElement,
   *  drawerCaptionSpan: HTMLElement,
   *  drawerTotalNode: HTMLElement | null
   *  sharedCart: any
   * }} refs
   */
  async #syncDrawerPricesFromShopify(content, { tableBody, drawerCaptionSpan, drawerTotalNode, sharedCart }) {
    try {
      // Wait until the Shopify session cart catches up with the shared storefront cart.
      // Without this, we can fetch /cart too early and read stale prices.
      await this.#waitForSessionCartSync(sharedCart);

      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const url = new URL(cartUrl, window.location.origin);
      // Cache-buster to avoid showing stale pricing after quick updates.
      url.searchParams.set('_', String(Date.now()));

      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Update total (caption + estimated total node)
      const serverTotalNode = doc.querySelector('text-component[ref="cartTotal"]') || doc.querySelector('.cart__total-value');
      const serverTotalText = (serverTotalNode?.textContent || '').trim();
      if (serverTotalText) {
        if (drawerCaptionSpan) drawerCaptionSpan.textContent = serverTotalText;
        if (drawerTotalNode) {
          drawerTotalNode.textContent = serverTotalText;
          drawerTotalNode.setAttribute('value', serverTotalText.replace(/\s+/g, ' ').trim());
        }
      }

      // Update tax note if present (Markets "Taxes and duties" toggle).
      const serverTaxNote = doc.querySelector('.cart__summary-item.tax-note');
      const drawerTaxNote = content.querySelector('.cart__summary-item.tax-note');
      if (serverTaxNote instanceof HTMLElement && drawerTaxNote instanceof HTMLElement) {
        drawerTaxNote.innerHTML = serverTaxNote.innerHTML;
      }

      // Update per-line displayed prices (sale/compare-at or regular).
      const serverTableBody = doc.querySelector('.cart-items__table tbody');
      if (!serverTableBody) return;

      const serverRows = Array.from(serverTableBody.querySelectorAll('tr.cart-items__table-row'))
        .filter((row) => !row.classList.contains('cart-items__nested-line'));

      const drawerRows = Array.from(tableBody.querySelectorAll('tr.cart-items__table-row'))
        .filter((row) => !row.classList.contains('cart-items__nested-line'));

      // Map server rows by variant id (avoids fragile "same index" assumptions).
      const serverRowsByVariantId = new Map();
      serverRows.forEach((serverRow) => {
        if (!(serverRow instanceof HTMLElement)) return;
        const variantId = serverRow.getAttribute('data-variant-id');
        if (!variantId) return;
        if (!serverRowsByVariantId.has(variantId)) {
          serverRowsByVariantId.set(variantId, serverRow);
        }
      });

      drawerRows.forEach((drawerRow) => {
        if (!(drawerRow instanceof HTMLElement)) return;
        const drawerVariantId = drawerRow.getAttribute('data-variant-id');
        if (!drawerVariantId) return;

        const serverRow = serverRowsByVariantId.get(drawerVariantId);
        if (!(serverRow instanceof HTMLElement)) return;

        const serverDetailsTd = serverRow.querySelector('td.cart-items__details');
        if (!(serverDetailsTd instanceof HTMLElement)) return;

        // Source of truth for current line price: the server-rendered "product total" column.
        // This is the most stable value across discount/tax display permutations.
        const serverRightPriceNode = serverRow.querySelector(
          'td.cart-items__price text-component, td.cart-items__price'
        );
        const serverCurrentPriceText = (serverRightPriceNode?.textContent || '').trim();

        const serverCompareAt = serverDetailsTd.querySelector('s.compare-at-price');
        const serverCompareAtText = (serverCompareAt?.textContent || '').trim();

        // 1) Update the right-side total column.
        const drawerRightTd = drawerRow.querySelector('td.cart-items__price');
        if (drawerRightTd && serverCurrentPriceText) {
          const span = drawerRightTd.querySelector('span:not(.visually-hidden)');
          if (span) span.textContent = serverCurrentPriceText;
        }

        // 2) Copy the entire details-area price block from the server HTML.
        //    This ensures unit prices and compare-at prices reflect Markets/discounts.
        const drawerDetailsTd = drawerRow.querySelector('td.cart-items__details');
        if (drawerDetailsTd && serverDetailsTd) {
          // Find the price div in the server HTML — it's the div containing
          // the unit price span and optional <s class="compare-at-price">.
          // We look for a div that has a visible span but is NOT part of the
          // product title or variant name.
          const serverPriceDiv = (() => {
            // The price block is the last <div> child of the details td
            // that contains price-related spans (not the title <p> or variant <p>).
            const divs = serverDetailsTd.querySelectorAll(':scope > div');
            for (const div of divs) {
              if (div.querySelector('s.compare-at-price') ||
                  div.querySelector('span.visually-hidden')) {
                return div;
              }
            }
            return null;
          })();

          if (serverPriceDiv) {
            // Find or create a matching container in the drawer row
            const existingPriceDiv = drawerDetailsTd.querySelector('.cart-items__price-wrapper') ||
              (() => {
                // Look for the price div the same way — last div with price spans
                const divs = drawerDetailsTd.querySelectorAll(':scope > div');
                for (const div of divs) {
                  if (div.querySelector('s.compare-at-price') ||
                      div.querySelector('.cart-items__sale-price') ||
                      div.querySelector('span.visually-hidden')) {
                    return div;
                  }
                }
                return null;
              })();

            if (existingPriceDiv) {
              existingPriceDiv.innerHTML = serverPriceDiv.innerHTML;
            } else {
              // No existing price block (we cleared it) — append the server one
              const clone = serverPriceDiv.cloneNode(true);
              drawerDetailsTd.appendChild(clone);
            }
          }
        }
      });
    } catch (e) {
      // If the server fetch fails, we keep the current shared-cart rendering.
      // eslint-disable-next-line no-console
      console.error('Failed to sync drawer prices from Shopify', e);
    }
  }

  /**
   * Waits until /cart.js has the same variant quantities as the shared cart.
   *
   * @param {any} sharedCart
   */
  async #waitForSessionCartSync(sharedCart) {
    const targetMap = this.#buildVariantQtyMapFromSharedCart(sharedCart);
    if (targetMap.size === 0) return;

    const attempts = 10;
    const delayMs = 150;

    for (let i = 0; i < attempts; i++) {
      try {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const sessionCart = await res.json();
        const sessionMap = this.#buildVariantQtyMapFromSessionCart(sessionCart);

        if (this.#mapsEqual(targetMap, sessionMap)) {
          return;
        }
      } catch (e) {
        // keep retrying
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  /**
   * @param {any} sharedCart
   * @returns {Map<number, number>}
   */
  #buildVariantQtyMapFromSharedCart(sharedCart) {
    const result = new Map();
    const edges = sharedCart?.lines?.edges || [];
    edges.forEach((edge) => {
      const node = edge?.node;
      const qty = Number(node?.quantity || 0);
      const gid = node?.merchandise?.id || '';
      const variantId = parseInt(String(gid).split('/').pop(), 10);
      if (!Number.isFinite(variantId) || variantId <= 0 || qty <= 0) return;
      result.set(variantId, (result.get(variantId) || 0) + qty);
    });
    return result;
  }

  /**
   * @param {any} sessionCart
   * @returns {Map<number, number>}
   */
  #buildVariantQtyMapFromSessionCart(sessionCart) {
    const result = new Map();
    const items = sessionCart?.items || [];
    items.forEach((item) => {
      const variantId = Number(item?.variant_id || 0);
      const qty = Number(item?.quantity || 0);
      if (!Number.isFinite(variantId) || variantId <= 0 || qty <= 0) return;
      result.set(variantId, (result.get(variantId) || 0) + qty);
    });
    return result;
  }

  /**
   * @param {Map<number, number>} a
   * @param {Map<number, number>} b
   */
  #mapsEqual(a, b) {
    if (a.size !== b.size) return false;
    for (const [key, val] of a.entries()) {
      if (b.get(key) !== val) return false;
    }
    return true;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
